/* eslint-disable i18n-scan/no-inline-content */
import {
  BodyText,
  CaptionText,
  Card,
  Col,
  Divider,
  HeadingText,
  Row,
  Tooltip,
} from '@paypalcorp/pp-react'
import { isEmpty, merge } from 'lodash-es'
import {
  getCurrencyFormat,
  getLocalizedText,
} from 'ppbl/utils/locale-helper.js'
import { interpolate } from 'ppbl/utils/string-helper'
import React, { useContext } from 'react'
import { toCommaSeparatedNumber } from '../__utils__.js'
import { OfferContext } from './offerContext'
import TermInputRadioV2 from './termInputRadioV2'
import { trackClick } from 'ppbl/utils/fpti-helper'
import { FPTI_EVENTS } from 'ppbl/utils/fptiEventNames'

type MetricProps = {
  infoLabel: string
  value?: string
  valueType?: string
  labelType?: string
  toolTipText?: string
  contrast?: boolean
}

export const OfferInfoMetricsV2: React.FC<MetricProps> = ({
  infoLabel,
  value,
  labelType,
  valueType,
  contrast,
  toolTipText,
}) => {
  const renderLabel = () => {
    switch (labelType) {
      case 'strong':
        return (
          <BodyText className={`${contrast && 'metricTextContrastV2'}`} strong>
            {infoLabel}
          </BodyText>
        )
      case 'caption':
        return (
          <CaptionText className={`${contrast && 'metricTextContrastV2'}`}>
            {infoLabel}
          </CaptionText>
        )
      default:
        return (
          <BodyText className={`${contrast && 'metricTextContrastV2'}`}>
            {infoLabel}
          </BodyText>
        )
    }
  }

  const renderValue = () => {
    switch (valueType) {
      case 'strong':
        return (
          <HeadingText
            className={`${contrast && 'metricTextContrastV2'}`}
            size="title"
          >
            {value}
          </HeadingText>
        )
      case 'caption':
        return (
          <CaptionText className={`${contrast && 'metricTextContrastV2'}`}>
            {value}
          </CaptionText>
        )
      default:
        return (
          <BodyText className={`${contrast && 'metricTextContrastV2'}`}>
            {value}
          </BodyText>
        )
    }
  }

  return (
    <div className="infoMetricsContainerV2">
      <Row id={infoLabel}>
        <Col className="cardColumnLeftV2" xs={8} sm={8}>
          {renderLabel()}
          {toolTipText && (
            <Tooltip
              className={labelType === 'caption' ? 'captionTooltipV2' : ''}
              description={toolTipText}
            />
          )}
        </Col>
        <Col className="cardColumnRightV2" xs={4} sm={4}>
          {value && <div>{renderValue()}</div>}
        </Col>
      </Row>
    </div>
  )
}

/**
 * This is a functional component that display list of offer metrics
 * @returns {JSX.Element} - A JSX element composed of offer metrics component
 */
const OfferInfoV2: React.FC = () => {
  const {
    offerValues,
    loanTerm,
    loanAmount,
    setLoanTerm,
    offerOptions,
    renewalStatus,
    previousLoans,
    previousLoanOutstandingAmount,
    fptiData,
    applicationId,
  } = useContext(OfferContext)

  function formattedCurrency(value) {
    return getCurrencyFormat(Number(value))
  }

  const handleSelection = (e) => {
    let selectedTerms = e.target.value
    setLoanTerm(selectedTerms)
    trackClick(
      merge(fptiData, {
        component: `offerTerm_V2_${selectedTerms}_selected`,
        link: `offerTerm_V2_${selectedTerms}_selected`,
        applicationId,
        event_name: FPTI_EVENTS.CLICK.OFFER.TERM_SELECTED.V2,
      }),
    )
  }

  let isSelected = true

  return (
    <div className="offerInfoContainerV2">
      <div className="radioGroupV2">
        {offerOptions.map(
          (
            { label: loanTermLable, value: loanTermValue, maxInput: loanLimit },
            index,
          ) => {
            isSelected = loanTermValue === String(loanTerm)

            return (
              <TermInputRadioV2
                handleSelection={handleSelection}
                loanTermLable={loanTermLable}
                loanTermValue={loanTermValue}
                isSelected={isSelected}
                loanAmount={parseFloat(loanAmount)}
                loanLimit={loanLimit}
                ind={index}
                key={index}
              />
            )
          },
        )}
      </div>
      {renewalStatus === 'LOAN_RENEWAL' && !isEmpty(previousLoans) ? (
        <>
          <HeadingText size="title">Transaction summary</HeadingText>
          <div className="infoCardContainerV2">
            <Card className="infoCardWrapperV2">
              <div className="transactionSummarySectionV2">
                <OfferInfoMetricsV2
                  infoLabel={getLocalizedText(
                    'offer-page.offerInfo.infoMetricLabel1RenewalOpt2',
                  )}
                  value={
                    offerValues.offerAmount &&
                    formattedCurrency(offerValues.offerAmount)
                  }
                />
                <OfferInfoMetricsV2
                  infoLabel={interpolate(
                    getLocalizedText(
                      'offer-page.offerInfo.infoMetricLabel2RenewalOpt2',
                    ),
                    {
                      loanAccountNumbers: (
                        <span>
                          {previousLoans
                            .map((each) => {
                              return `#${each.id}`
                            })
                            .join(' ')}
                        </span>
                      ),
                    },
                  )}
                />
                <OfferInfoMetricsV2
                  infoLabel={getLocalizedText(
                    'offer-page.offerInfo.infoMetricLabel3RenewalOpt2',
                  )}
                  contrast
                  value={
                    previousLoanOutstandingAmount &&
                    `-$${toCommaSeparatedNumber(previousLoanOutstandingAmount)}`
                  }
                />
              </div>
              <Divider className="dividerV2" />
              <div className="cardFootingV2">
                <OfferInfoMetricsV2
                  infoLabel={getLocalizedText(
                    'offer-page.offerInfo.infoMetricLabel4RenewalOpt2',
                  )}
                  valueType="strong"
                  value={
                    offerValues.offerAmount &&
                    previousLoanOutstandingAmount &&
                    `$${toCommaSeparatedNumber(
                      offerValues.offerAmount - previousLoanOutstandingAmount,
                    )}`
                  }
                />
              </div>
            </Card>
          </div>
        </>
      ) : (
        <></>
      )}
    </div>
  )
}

export default OfferInfoV2
